import { Injectable } from "@angular/core";
import { CustomerLoginResponse } from "../../shared/models/customer.model";
import { environment } from "../../environments/environment";

export interface CustomerLoginResult {
  response: CustomerLoginResponse;
}

/**
 * Comunicación con los endpoints de autenticación del BFF.
 *
 * Flujo de autenticación basado en cookies HttpOnly:
 *  - Login:   GET  /v1/customer/login/{email}  → BFF setea access_token, refresh_token, xsrf-token
 *  - Refresh: POST /v1/customer/refresh        → BFF rota las cookies (requiere X-XSRF-TOKEN)
 *  - Logout:  POST /v1/customer/logout         → BFF limpia las cookies (requiere X-XSRF-TOKEN)
 *
 * El frontend nunca lee ni almacena tokens — solo envía `credentials: 'include'`
 * para que el browser incluya las cookies en cada request.
 *
 * CSRF: el BFF emite también una cookie `xsrf-token` NO httpOnly que Angular puede
 * leer desde document.cookie y enviar como header X-XSRF-TOKEN en operaciones de
 * escritura (double-submit pattern).
 */
@Injectable({ providedIn: "root" })
export class BffAuthService {
  private readonly base = environment.bffEndpoint;

  /**
   * Autentica al usuario. El BFF setea las cookies de sesión en la respuesta.
   * Devuelve los datos del usuario (customer + invUser) para construir el estado local.
   */
  async login(email: string, password: string): Promise<CustomerLoginResult> {
    const url = `${this.base}/v1/customer/login/${encodeURIComponent(email)}`;

    let response: Response;
    try {
      response = await fetch(url, {
        method:      "GET",
        credentials: "include",
        headers: {
          Authorization: `Basic ${btoa(`${email}:${password}`)}`,
          "product-id":  String(environment.bffProductId),
          "Content-Type": "application/json",
        },
      });
    } catch {
      throw new Error("No se pudo conectar con el servidor. Verifica tu conexión.");
    }

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        throw new Error("Correo o contraseña incorrectos.");
      }
      throw new Error(`Error del servidor (${response.status}). Intenta más tarde.`);
    }

    let body: CustomerLoginResponse;
    try {
      const raw = await response.json();
      // El BFF puede devolver un array con un único elemento o el objeto directamente
      body = Array.isArray(raw) ? raw[0] : raw;
    } catch {
      throw new Error("Respuesta inesperada del servidor.");
    }

    if (!body?.success || !body?.data?.customer || !body?.data?.invUser) {
      throw new Error("Respuesta de autenticación inválida.");
    }

    return { response: body };
  }

  /**
   * Rota los tokens de sesión. El browser incluye automáticamente la cookie
   * `refresh_token` gracias a `credentials: 'include'`.
   * Requiere `X-XSRF-TOKEN` para el patrón CSRF double-submit.
   */
  async refresh(): Promise<void> {
    const url = `${this.base}/v1/customer/refresh`;

    let response: Response;
    try {
      response = await fetch(url, {
        method:      "POST",
        credentials: "include",
        headers: {
          "X-XSRF-TOKEN": this.getXsrfToken(),
          "Content-Type": "application/json",
        },
      });
    } catch {
      throw new Error("No se pudo renovar la sesión. Verifica tu conexión.");
    }

    if (!response.ok) {
      throw new Error(`No se pudo renovar la sesión (${response.status}).`);
    }
  }

  /**
   * Cierra sesión en el BFF. El BFF limpia las cookies httpOnly.
   * Requiere `X-XSRF-TOKEN` para el patrón CSRF double-submit.
   */
  async logout(): Promise<void> {
    const url = `${this.base}/v1/customer/logout`;

    try {
      await fetch(url, {
        method:      "POST",
        credentials: "include",
        headers: {
          "X-XSRF-TOKEN": this.getXsrfToken(),
          "Content-Type": "application/json",
        },
      });
    } catch {
      // Ignorar errores de red en logout: el estado local se limpia igualmente
    }
  }

  /**
   * Lee el valor de la cookie `xsrf-token` (no httpOnly) para enviarlo
   * como header `X-XSRF-TOKEN` en operaciones de escritura (CSRF double-submit).
   */
  private getXsrfToken(): string {
    if (typeof document === "undefined") return "";
    const match = document.cookie.match(/(?:^|;\s*)xsrf-token=([^;]+)/);
    return match ? decodeURIComponent(match[1]) : "";
  }
}
